// ------------------------- v2 data utility functions ------------------------

import { CollectionProduct } from '@portal/api/shopifyV2/types';
import {
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53,
  ANNUAL_PLAN_VARIANT_ID,
  VariantIdObject
} from '@portal/config/base';
import { AlternateFlavor } from '@portal/processes/ShopV2/ShopV2.types';
import type { GetAddressesResponse } from 'api/shopify/get-addresses/types';
import type { GetCountriesResponse } from 'api/shopify/get-countries/types';
import {
  MAX_PRODUCTS_IN_SUBSCRIPTION_QUANTITY,
  nextOrderOnlyProductsForStores
} from 'processes/Home/constants';
import { StoreCode } from 'schema/storeCode';
import {
  mapDoubleIdsToSingle,
  mapDiscountedProducts
} from 'shared/components/V53EditProductsModal/utils';
import { getFormattedShopifyId, getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import type { ShopifyCountry } from 'state/internal/getShopifyCountries/types';
import type {
  GetSubscriptionDetailsV2InitialResponse,
  PaymentMethodV2,
  ShippingAddressListV2,
  SubscriptionDetailsV2Response,
  SubscriptionLineItemV2
} from 'state/internal/getSubscriptionDetails/types';
import type { SubscriptionsResponseV2 } from 'state/internal/getSubscriptions/types';
import type { PutSubscriptionDetailsV2InitialResponse } from 'state/internal/updateSubscriptionDetails/types';

// returns the shopify contract ID from the complete GUID
const getShopifyContractId = (completeShopifyGuid: string) =>
  `${getIdFromShopifyFormattedId(completeShopifyGuid)}` || '';

const getShopifyVariantId = (completeVariantId: string) =>
  `${getIdFromShopifyFormattedId(completeVariantId)}` || '';

// return either the custom name of subscription or the shopify contract ID
const getSubscriptionName = (subscription: SubscriptionDetailsV2Response) =>
  subscription.custom_name ||
  `#${getShopifyContractId(subscription.line_items[0].shopify_contract_id || '')}`;

// returns whether the subscription is ACTIVE or CANCELLED
const isSubscriptionActiveV2 = (subscriptionData: SubscriptionDetailsV2Response) =>
  subscriptionData.is_active;

// returns if it is an annual plan subscription
const isSubscriptionAnnualPlan = (
  store: StoreCode,
  subscriptionData: SubscriptionDetailsV2Response
) => {
  const annualPlanShortId = ANNUAL_PLAN_VARIANT_ID[store]?.default;
  const hasApProduct = !!subscriptionData?.line_items?.find((lineItem) => {
    const productVariantId = lineItem?.product?.shopify_variant_id;
    return annualPlanShortId && productVariantId && productVariantId?.includes(annualPlanShortId);
  });

  return hasApProduct;
};

// formats displayable text such that only first letter is capitalized
const formatRenderableText = (dateString: string) =>
  `${dateString.charAt(0).toUpperCase()}${dateString.slice(1).toLowerCase()}`;

// formats the complete next order banner text
export const getNextOrderSwitchMessage = (
  quantity: number,
  nextOrderProductIsTravelPack: boolean,
  formattedNextOrderDate: string
) => {
  const getProductText = (qty: number, isTravelPack: boolean) => {
    if (qty > 1) {
      return isTravelPack ? 'boxes of Travel Packs' : 'Pouches';
    }
    return isTravelPack ? 'box of Travel Packs' : 'Pouch';
  };

  return `Your next order will ship ${quantity} ${getProductText(
    quantity,
    nextOrderProductIsTravelPack
  )}. On the following order ${formattedNextOrderDate}, your subscription will ship ${quantity} ${getProductText(
    quantity,
    !nextOrderProductIsTravelPack
  )}.`;
};

// returns the shopify payment method ID
const getPaymentMethodId = (paymentMethod: PaymentMethodV2) =>
  paymentMethod && paymentMethod.shopify_payment_method_id
    ? `${getIdFromShopifyFormattedId(paymentMethod.shopify_payment_method_id) || ''}`
    : undefined;

const includesVariant = (variantToCheck: string, variants: VariantIdObject) => {
  return (
    variantToCheck.includes(variants.default) ||
    (variants.freeShipping && variantToCheck.includes(variants.freeShipping)) ||
    (variants.tropical && variantToCheck.includes(variants.tropical))
  );
};

const getLineItemIdByVariantId = (
  subscription: SubscriptionDetailsV2Response,
  variantId: string,
  isOneTime: boolean = false
) => {
  const lineItem = subscription.line_items.find(
    (item) =>
      includesVariant(getShopifyVariantId(item.product.shopify_variant_id), {
        default: variantId
      }) && item.is_one_time === isOneTime
  );

  return lineItem?.shopify_line_item_id
    ? `${getIdFromShopifyFormattedId(lineItem.shopify_line_item_id)}`
    : '';
};

const isFirstVariantSubscription = (
  subscription: SubscriptionDetailsV2Response,
  store: StoreCode
) => {
  return subscription.line_items.find((lineItem) => {
    const variantId = getShopifyVariantId(lineItem.product.shopify_variant_id);
    return (
      includesVariant(variantId, SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store]) ||
      includesVariant(variantId, SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID[store])
    );
  });
};

const isSecondVariantSubscription = (
  subscription: SubscriptionDetailsV2Response,
  store: StoreCode
) => {
  return subscription.line_items.find((lineItem) => {
    const variantId = getShopifyVariantId(lineItem.product.shopify_variant_id);
    return (
      includesVariant(variantId, SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]) ||
      includesVariant(variantId, SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID[store])
    );
  });
};

const isSingleSubCoreSubscriptionLineItem = (item: SubscriptionLineItemV2, store: StoreCode) => {
  const variantId = getShopifyVariantId(item.product.shopify_variant_id);
  return (
    includesVariant(variantId, SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store]) ||
    includesVariant(variantId, SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store])
  );
};

const isDoubleSubCoreSubscriptionLineItem = (item: SubscriptionLineItemV2, store: StoreCode) => {
  const variantId = getShopifyVariantId(item.product.shopify_variant_id);
  return (
    includesVariant(variantId, SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID[store]) ||
    includesVariant(variantId, SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID[store])
  );
};

const onlyHasOneSingleSubCoreSubscriptionLineItem = (
  items: SubscriptionLineItemV2[],
  store: StoreCode
) => {
  return (
    items.filter((item) => isSingleSubCoreSubscriptionLineItem(item, store)).length === 1 &&
    items.filter((item) => isDoubleSubCoreSubscriptionLineItem(item, store)).length === 0
  );
};

const getSubscriptionVariantIdByQuantity = (
  initialVariantId: string,
  store: StoreCode,
  quantity: number,
  options: {
    useShortVariantId?: boolean;
    useFreeShipping?: boolean;
    useTropical?: boolean;
    useForceDouble?: boolean;
  } = {
    useShortVariantId: false,
    useFreeShipping: false,
    useTropical: false,
    useForceDouble: false
  }
) => {
  let variantIdObject: VariantIdObject;
  if (
    includesVariant(initialVariantId, SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store]) ||
    includesVariant(initialVariantId, SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID[store])
  ) {
    variantIdObject =
      quantity === 1
        ? SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store]
        : SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID[store];
  } else if (
    includesVariant(initialVariantId, SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]) ||
    includesVariant(initialVariantId, SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID[store])
  ) {
    variantIdObject =
      quantity === 1
        ? SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]
        : SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID[store];
  } else if (
    includesVariant(initialVariantId, SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]) ||
    includesVariant(initialVariantId, SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store])
  ) {
    variantIdObject =
      quantity === 1 && !options.useForceDouble
        ? SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]
        : SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store];
  } else if (
    includesVariant(initialVariantId, SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]) ||
    includesVariant(initialVariantId, SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store])
  ) {
    variantIdObject =
      quantity === 1 && !options.useForceDouble
        ? SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]
        : SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store];
  } else {
    return getFormattedShopifyId('ProductVariant', initialVariantId);
  }

  let variantId = variantIdObject.default;

  if (options.useFreeShipping && variantIdObject.freeShipping) {
    variantId = variantIdObject.freeShipping;
  }

  if (options.useTropical && variantIdObject.tropical) {
    variantId = variantIdObject.tropical;
  }

  // const variantId =
  //   options.useFreeShipping && variantIdObject.freeShipping
  //     ? variantIdObject.freeShipping
  //     : variantIdObject.default;

  if (options.useShortVariantId) {
    return variantId;
  }

  return getFormattedShopifyId('ProductVariant', variantId);
};

// formats the shopify response to what the existing
// UI is capable of digesting
const formatCountryData = (data: GetCountriesResponse) => {
  const countries = data.countries
    .map((country) => ({
      code: country.code,
      name: country.name,
      zones: country?.provinces || [],
      labels: {},
      formatting: {
        edit: '{firstName}{lastName}_{company}_{address1}_{address2}_{zip}{city}{province}_{country}_{phone}'
      }
    }))
    .flat() as ShopifyCountry[];

  return { countries };
};

// formats the shopify response to what the existing
// UI is capable of digesting
const formatAddressData = (addresses: GetAddressesResponse) =>
  addresses.map((address) => ({
    id: address.id,
    address_1: address.address1,
    address_2: address.address2,
    city: address.city,
    company: address.company,
    first_name: address.firstName,
    last_name: address.lastName,
    province: address.province,
    province_code: address.provinceCode,
    country: address.country,
    country_code: address.countryCode,
    zip: address.zip,
    phone: address.phone,
    name: `${address.firstName} ${address.lastName}`
  })) as ShippingAddressListV2;

// formats the subscription data by
// 1) separate line items into "subscription only" items and "next order only" items
//    both are inserted into the initial subscription response
const formatSubscriptionData = (
  initialSubscriptionData:
    | GetSubscriptionDetailsV2InitialResponse
    | PutSubscriptionDetailsV2InitialResponse
) => {
  const subscriptionOnlyItems = initialSubscriptionData.line_items.filter(
    (item) => !item.is_one_time
  );
  const nextOrderOnlyItems = initialSubscriptionData.line_items.filter((item) => item.is_one_time);

  return {
    ...initialSubscriptionData,
    frequency_interval: formatRenderableText(initialSubscriptionData.frequency_interval),
    subscription_only_items: subscriptionOnlyItems,
    next_order_only_items: nextOrderOnlyItems
  } as SubscriptionDetailsV2Response;
};

const formatSubscriptionListData = (
  initialSubscriptionListData:
    | GetSubscriptionDetailsV2InitialResponse[]
    | PutSubscriptionDetailsV2InitialResponse[]
) => {
  return initialSubscriptionListData.map((subscription) => formatSubscriptionData(subscription));
};

const findSubscriptionByContractId = (
  subscriptions: SubscriptionDetailsV2Response[],
  contractId: string
) => {
  return subscriptions.find(
    (subscription) => getShopifyContractId(subscription.shopify_contract_id) === contractId
  );
};

const getProductQuantity = (productId: string, products: SubscriptionLineItemV2[]): number => {
  const product = products?.find((p) => p.shopify_contract_id === productId);
  return product === undefined ? 0 : product.quantity;
};

const checkAllSubscriptionsHaveMaxQuantityOfProduct = (
  subscriptionItems: SubscriptionsResponseV2,
  productId: string
): boolean => {
  return subscriptionItems.every(
    (subscription) =>
      getProductQuantity(productId, subscription.line_items) >=
      MAX_PRODUCTS_IN_SUBSCRIPTION_QUANTITY
  );
};

const checkAllOneTimeHaveMaxQuantityOfProduct = (
  subscriptionItems: SubscriptionsResponseV2,
  productId: string
): boolean => {
  const maxQuantity = MAX_PRODUCTS_IN_SUBSCRIPTION_QUANTITY;

  return subscriptionItems.every(
    (subscription) =>
      subscription.next_order_only_items !== null &&
      getProductQuantity(productId, subscription.next_order_only_items) >= maxQuantity
  );
};

const checkIfSubscriptionAlreadyHasProduct = (
  productVariantIds: string[],
  product: CollectionProduct,
  selectedFlavorVariant: AlternateFlavor,
  store: StoreCode
): boolean => {
  const foundId = productVariantIds?.find((id) => {
    const variantId = getIdFromShopifyFormattedId(product.variants[0].id);
    const currentId = selectedFlavorVariant
      ? getIdFromShopifyFormattedId(selectedFlavorVariant?.variant?.variants?.[0].id)
      : variantId;

    const existsAsMappedToSingle =
      mapDoubleIdsToSingle(getIdFromShopifyFormattedId(id) || '', store) === currentId;
    const existsAsDiscounted =
      mapDiscountedProducts(id, store) === mapDiscountedProducts(variantId, store);

    return existsAsMappedToSingle || existsAsDiscounted;
  });

  return Boolean(foundId);
};

const isForNextOrderOnly = (productId: number, store: StoreCode) =>
  nextOrderOnlyProductsForStores[store].includes(productId);

export {
  checkAllOneTimeHaveMaxQuantityOfProduct,
  checkAllSubscriptionsHaveMaxQuantityOfProduct,
  checkIfSubscriptionAlreadyHasProduct,
  findSubscriptionByContractId,
  formatAddressData,
  formatCountryData,
  formatRenderableText,
  formatSubscriptionData,
  formatSubscriptionListData,
  getLineItemIdByVariantId,
  getPaymentMethodId,
  getShopifyContractId,
  getShopifyVariantId,
  getSubscriptionName,
  getSubscriptionVariantIdByQuantity,
  includesVariant,
  isDoubleSubCoreSubscriptionLineItem,
  isFirstVariantSubscription,
  isForNextOrderOnly,
  isSecondVariantSubscription,
  isSingleSubCoreSubscriptionLineItem,
  isSubscriptionActiveV2,
  isSubscriptionAnnualPlan,
  onlyHasOneSingleSubCoreSubscriptionLineItem,
  getFormattedShopifyId
};
